/**
 * Product detail modal with full competitive intelligence data
 * Spike 13: Enhanced with tabs for Competitors, Compare, and Assessment
 *
 * Sub-components extracted to ./product-detail/ for maintainability:
 * - MetricDisplay, CapabilityBadge, CompetitorCard, SentimentCard (presentational)
 * - OverviewTab, CompetitorsTab, CompareTab, AssessmentTab (tab content)
 * - ProductHeader, DataQualityFooter (modal sections)
 */

import { useEffect, useState } from 'react';
import { api } from '../../services/api';
import type { ProductProfile } from '../../types';

import {
  OverviewTab,
  CompetitorsTab,
  CompareTab,
  AssessmentTab,
  ProductHeader,
  DataQualityFooter,
} from './product-detail';

type ProductModalTab = 'overview' | 'competitors' | 'compare' | 'assessment';

interface ProductDetailModalProps {
  companyId: string;
  productId: string | null;
  onClose: () => void;
}

const TABS = [
  { id: 'overview' as const, label: 'Overview' },
  { id: 'competitors' as const, label: 'Competitors' },
  { id: 'compare' as const, label: 'Compare' },
  { id: 'assessment' as const, label: 'Assessment' },
];

export function ProductDetailModal({ companyId, productId, onClose }: ProductDetailModalProps) {
  const [data, setData] = useState<ProductProfile | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ProductModalTab>('overview');
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<string | null>(null);
  const [selectedCompetitorName, setSelectedCompetitorName] = useState<string>('');

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && productId) onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [productId, onClose]);

  useEffect(() => {
    if (!productId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    api
      .getProductProfile(companyId, productId)
      .then((result) => {
        setData(result);
        setLoading(false);
      })
      .catch((err: unknown) => {
        setError(err instanceof Error ? err.message : 'Failed to load product details');
        setLoading(false);
      });
  }, [companyId, productId]);

  if (!productId) return null;

  const handleSelectCompetitor = (id: string) => {
    setSelectedCompetitorId(id);
    const competitor = data?.primary_competitors.find((_, idx) => `existing-${idx}` === id);
    setSelectedCompetitorName(competitor?.name || id);
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="product-modal-title"
      data-testid="product-detail-modal"
    >
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative w-full max-w-4xl bg-white rounded-xl shadow-2xl max-h-[90vh] overflow-y-auto">
          <button
            onClick={onClose}
            className="absolute right-4 top-4 text-gray-400 hover:text-gray-600 z-10"
            aria-label="Close modal"
          >
            <svg
              className="h-6 w-6"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>

          {loading && (
            <div className="p-8 animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-3/4" />
              <div className="h-4 bg-gray-200 rounded w-full" />
              <div className="h-4 bg-gray-200 rounded w-2/3" />
              <div className="grid grid-cols-3 gap-4 mt-6">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-24 bg-gray-200 rounded" />
                ))}
              </div>
            </div>
          )}

          {error && (
            <div className="p-8 text-center text-red-600">
              <p>{error}</p>
              <button
                onClick={onClose}
                className="mt-4 px-4 py-2 bg-gray-100 rounded hover:bg-gray-200"
              >
                Close
              </button>
            </div>
          )}

          {data && (
            <div className="p-6 space-y-6">
              <ProductHeader data={data} />

              <div className="border-b border-gray-200">
                <nav className="flex space-x-4" aria-label="Product tabs">
                  {TABS.map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`py-2 px-1 text-sm font-medium border-b-2 -mb-px transition-colors ${
                        activeTab === tab.id
                          ? 'border-primary-500 text-primary-600'
                          : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                      }`}
                      aria-current={activeTab === tab.id ? 'page' : undefined}
                    >
                      {tab.label}
                    </button>
                  ))}
                </nav>
              </div>

              {activeTab === 'overview' && <OverviewTab data={data} />}

              {activeTab === 'competitors' && (
                <CompetitorsTab
                  productId={data.id}
                  productName={data.name}
                  existingCompetitors={data.primary_competitors}
                  onSelectCompetitor={handleSelectCompetitor}
                  selectedCompetitorId={selectedCompetitorId}
                />
              )}

              {activeTab === 'compare' && selectedCompetitorId && (
                <CompareTab
                  productId={data.id}
                  productName={data.name}
                  competitorId={selectedCompetitorId}
                  competitorName={selectedCompetitorName}
                />
              )}

              {activeTab === 'compare' && !selectedCompetitorId && (
                <div className="text-center py-8">
                  <p className="text-gray-500">
                    Select a competitor from the Competitors tab first.
                  </p>
                  <button
                    onClick={() => setActiveTab('competitors')}
                    className="mt-4 text-primary-600 hover:text-primary-800 font-medium"
                  >
                    Go to Competitors
                  </button>
                </div>
              )}

              {activeTab === 'assessment' && (
                <AssessmentTab productId={data.id} productName={data.name} />
              )}

              <DataQualityFooter
                completeness={data.data_completeness}
                lastUpdated={data.last_updated}
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ProductDetailModal;
